import React, { useEffect, useState } from 'react';
import { Lock, Sparkles, Check } from 'lucide-react';

const BROWSE_LIMIT_MS = 8 * 60 * 1000; // 8 minutes of free browsing for visitors
const TRIGGERED_KEY = 'visitor_timeout_triggered'; // once true, modal shows forever

/*
 * Visitor browsing timer.
 *
 * Anonymous visitors get 1 minute of free browsing on the landing page.
 * After that the screen freezes with a non-dismissible signup CTA.
 *
 * Once triggered, we persist a flag so the modal shows IMMEDIATELY on every
 * subsequent page load/navigation — reloading or navigating around does not
 * give more free minutes.
 *
 * Logged-in users never see this modal. Signing up/logging in clears the flag.
 */
const VisitorTimeoutModal = ({ onSignup, onLogin }) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Skip entirely for logged-in users.
    if (localStorage.getItem('token')) return;

    // Already triggered previously → lock immediately, no free time.
    if (localStorage.getItem(TRIGGERED_KEY) === 'true') {
      setShow(true);
      document.body.style.overflow = 'hidden';
      return;
    }

    const timer = setTimeout(() => {
      localStorage.setItem(TRIGGERED_KEY, 'true');
      setShow(true);
      document.body.style.overflow = 'hidden';
    }, BROWSE_LIMIT_MS);

    return () => {
      clearTimeout(timer);
      // Only restore scroll if the modal was never shown.
      if (!localStorage.getItem(TRIGGERED_KEY)) {
        document.body.style.overflow = '';
      }
    };
  }, []);

  // Prevent keyboard / interaction bypass when modal is locked.
  useEffect(() => {
    if (!show) return;
    const stop = (e) => {
      if (e.target.closest('[data-testid="visitor-timeout-modal"]')) return;
      e.stopPropagation();
      e.preventDefault();
    };
    document.addEventListener('keydown', stop, true);
    document.addEventListener('touchstart', stop, { passive: false, capture: true });
    return () => {
      document.removeEventListener('keydown', stop, true);
      document.removeEventListener('touchstart', stop, { capture: true });
    };
  }, [show]);

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      data-testid="visitor-timeout-modal"
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="bg-gradient-to-br from-[#FF6600] to-[#FF8533] p-6 text-white text-center">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Lock size={30} />
          </div>
          <h2 className="text-xl font-bold">Loving what you see?</h2>
          <p className="text-white/95 text-sm mt-1">
            Sign up to continue exploring NutriFitness AI
          </p>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300 text-center">
            Get <strong>7 days free</strong>, then continue with the plan that fits you.
            Cancel anytime before day 7 — no charge.
          </p>

          <div className="space-y-2">
            <Perk text="AI-generated meal + workout plans" />
            <Perk text="Unlimited food scans" />
            <Perk text="3D exercise demos with form guidance" />
            <Perk text="Personalized AI coaching" />
            <Perk text="Cancel anytime, full transparency" />
          </div>

          <button
            data-testid="visitor-timeout-signup"
            onClick={onSignup}
            className="w-full py-3 bg-gradient-to-r from-[#FF6600] to-[#FF8533] text-white font-bold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2"
          >
            <Sparkles size={18} />
            Start free trial
          </button>

          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            Already have an account?{' '}
            <button
              data-testid="visitor-timeout-login"
              onClick={onLogin}
              className="text-[#FF6600] font-semibold hover:underline"
            >
              Log in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

const Perk = ({ text }) => (
  <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
    <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
      <Check size={12} className="text-green-600 dark:text-green-400" />
    </div>
    <span>{text}</span>
  </div>
);

export default VisitorTimeoutModal;
